import { defineStore } from 'pinia'
import axios from '@/config/axios'
import { isAxiosError } from 'axios'
import { useAuthStore } from './auth'
import { logger } from '../utils/logger'
import router from '@/router'
// Types
export interface PortfolioUserRef {
  id: number
  name?: string
  email?: string
}
export interface ReportingCurrency {
  id: number
  code: string
  name?: string
  symbol?: string
}

export interface Portfolio {
  id: number
  name: string
  initial_deposit: number | string
  user: PortfolioUserRef
  visibility: VisibilityType
  visibility_id: number
  reporting_currency?: ReportingCurrency | null
  reporting_currency_code?: string
  created_by: PortfolioUserRef | null
  updated_by: PortfolioUserRef | null
  created_at: string
  updated_at: string
  assets: PortfolioAsset[]
  assets_value: number
  total_invested: number | string
  cash_balance: number | string
  total_deposits: number | string
  total_withdrawals: number | string
  total_deposits_without_adjustments: number | string
  manual_deposits: number | string
  manual_withdrawals: number | string
  manual_deposits_net: number | string
  total_contribution: number | string
  total_value: number | string
  total_gain_loss: number | string
  total_gain_loss_percentage: number | string
  total_dividends?: number | string
  metrics?: {
    realized?: {
      amount: number | string
      percentage: number | string
    }
    unrealized?: {
      amount: number | string
      percentage: number | string
    }
  }
  is_default?: boolean
}
interface PortfolioAsset {
  id: number
  portfolio: number
  asset: Asset
  quantity: number
  average_purchase_price: number
  total_purchase_amount: number
  current_value: number
  current_price: number
  gain_loss: number
  gain_loss_percentage: number
  is_closed: boolean
  is_active?: boolean
  created_at: string
  updated_at: string
  last_update_date?: string | null
}
interface Asset {
  id: number
  name: string
  symbol: string
  asset_type: AssetType
  description: string
  current_value: number
  latest_values: AssetValue[]
  updated_at?: string
}
interface AssetType {
  id: number
  name: string
  description: string
}
interface AssetValue {
  date: string
  open: number
  high: number
  low: number
  close: number
  volume: number
  daily_change: number
  updated_at?: string
}
// Historical performance types
export interface HistoricalPerformancePoint {
  date: string
  value: number | string
  real_value?: number | string | null
}
export interface AssetAllocation {
  asset_name: string;
  asset_type: string;
  value: number;
  percentage: number;
}
export interface PortfolioPerformance {
  period: string;
  start_date: string;
  end_date: string;
  total_initial_value: number;
  total_current_value: number;
  total_performance: number;
  assets_performance: AssetPerformance[];
}
export interface AssetPerformance {
  asset_name: string;
  initial_value: number;
  current_value: number;
  performance: number;
}
// Diagnostics types pour un logging minimal des appels et états de chargement
interface FetchMetrics {
  network_requests: number
  cache_hits: number
  inflight_reuses: number
  errors: number
  last_ms?: number
}
interface DashboardInitMetrics {
  starts: number
  successes: number
  failures: number
  last_ms?: number
  last_portfolio?: number
}
export interface VisibilityType {
  id: number
  name: string
  description: string
  created_at?: string
  updated_at?: string
}
// Payload type for all TWR performances
export type AllTwrPayload = {
  ytd_performance: number
  mtd_performance: number
  wtd_performance: number
  ytd_start_date?: string
  mtd_start_date?: string
  wtd_start_date?: string
  reference_date?: string
  ytd_initial_value?: number
  ytd_final_value?: number
  mtd_initial_value?: number
  mtd_final_value?: number
  wtd_initial_value?: number
  wtd_final_value?: number
  // D-1 (variation du dernier jour coté précédent à la dernière date de cotation)
  d1_performance?: number
  d1_start_date?: string
  d1_end_date?: string
  d1_initial_value?: number
  d1_final_value?: number
  // Méthode de calcul D-1 (twr | cashflow_fallback)
  d1_method?: string | null
}
export interface DailyVariations {
  portfolio_name: string
  max_asset_daily_change: number
  min_asset_daily_change: number
}
export interface WeeklyVariations {
  portfolio_name: string
  max_asset_weekly_change: number
  min_asset_weekly_change: number
}
export interface MonthlyVariations {
  portfolio_name: string
  max_asset_monthly_change: number
  min_asset_monthly_change: number
}
export type PortfolioVariationsResult = DailyVariations | WeeklyVariations | MonthlyVariations
export interface RiskMetrics {
  sharpe_ratio: number | null
  sortino_ratio: number | null
  annualized_return?: number | null
  volatility: number | null
  volatility_standard: number | null
  volatility_conditional: number | null
  max_drawdown: number | null
  max_runup: number | null
  var: number | null
  cvar: number | null
  var_95: number | null
  cvar_95: number | null
  var_99: number | null
  cvar_99: number | null
  alpha?: number
  outliers_removed: number | null
  filtered_data_points: number | null
  returns_source?: string | null
  warning?: string
}
export interface PreviousDayVariation {
  variation_percent: number
  variation_value: number
  today_value: number
  yesterday_value: number
  variation_percent_with_cash?: number
  variation_value_with_cash?: number
  start_date?: string | null
  end_date?: string | null
  method?: string | null
  pending?: boolean
  previous_date?: string | null
  pending_message?: string | null
  has_data?: boolean
}
export interface BenchmarkData {
  portfolio_data: Array<{ date: string; value: number }>
  benchmark_data: Array<{ date: string; value: number }>
  cac_data: Array<{ date: string; value: number }>
  urth_data: Array<{ date: string; value: number }>
  qqq_data: Array<{ date: string; value: number }>
  alpha: number
  period: string
}
interface PeriodReturnBlock {
  return?: number
  start?: string
  end?: string
  start_value?: number
  end_value?: number
  method?: string | null
}
interface AllPeriodReturnsApiResponse {
  ytd?: PeriodReturnBlock
  mtd?: PeriodReturnBlock
  wtd?: PeriodReturnBlock
  d1?: PeriodReturnBlock
  as_of?: string
}
interface RiskMetricsApiResponse {
  sharpe_ratio?: number
  sortino_ratio?: number
  annualized_return?: number
  volatility?: number
  volatility_standard?: number
  volatility_conditional?: number
  max_drawdown?: number
  max_runup?: number
  var?: number
  cvar?: number
  var_95?: number
  cvar_95?: number
  var_99?: number
  cvar_99?: number
  alpha?: number
  outliers_removed?: number
  filtered_data_points?: number
  returns_source?: string
  warning?: string
}
interface PortfolioState {
  portfolios: Portfolio[]
  currentPortfolio: Portfolio | null
  isLoading: boolean
  visibilityTypes: VisibilityType[]
  error: string | null
  notFoundPortfolios: Set<number>
  // Dedup/cache helpers
  _portfoliosLastFetchedAt?: number
  _portfoliosInFlight?: Promise<void> | null
  _portfolioDetailsLastFetchedAt?: Record<number, number>
  _portfolioDetailsInFlight?: Map<number, Promise<Portfolio | null>>
  // Dashboard metrics caches (legacy TWR keys kept for compatibility during migration)
  _twrLastFetchedAt?: Record<number, number>
  _twrInFlight?: Map<number, Promise<AllTwrPayload>>
  _twrCache?: Record<number, AllTwrPayload>
  // New Period Returns caches (HPR)
  _periodReturnsLastFetchedAt?: Record<number, number>
  _periodReturnsInFlight?: Map<number, Promise<AllTwrPayload>>
  _periodReturnsCache?: Record<number, AllTwrPayload>
  _variationsLastFetchedAt?: Record<string, number>
  _variationsInFlight?: Map<string, Promise<PortfolioVariationsResult>>
  _riskMetricsLastFetchedAt?: Record<string, number>
  _riskMetricsInFlight?: Map<string, Promise<RiskMetrics>>
  _riskMetricsCache?: Record<string, RiskMetrics>
  _prevDayVarLastFetchedAt?: Record<number, number>
  _prevDayVarInFlight?: Map<number, Promise<PreviousDayVariation>>
  _prevDayVarCache?: Record<number, PreviousDayVariation>
  // Portfolio performance (asset_variations) caches
  _perfLastFetchedAt?: Record<string, number>
  _perfInFlight?: Map<string, Promise<PortfolioPerformance>>
  _perfCache?: Record<string, PortfolioPerformance>
  // Historical performance caches
  _histPerfLastFetchedAt?: Record<string, number>
  _histPerfInFlight?: Map<string, Promise<HistoricalPerformancePoint[]>>
  _firstValuationDateLastFetchedAt?: Record<number, number>
  _firstValuationDateInFlight?: Map<number, Promise<string | null>>
  // Benchmark data caches
  _benchmarkDataCache?: Record<string, { data: BenchmarkData; timestamp: number }>
  _benchmarkDataInFlight?: Record<string, Promise<BenchmarkData>>
  // Dashboard readiness per portfolio
  dashboardReadyByPortfolio?: Record<number, boolean>
  _dashboardInitInFlight?: Map<number, Promise<void>>
  // Diagnostics
  diagnostics?: {
    perf: FetchMetrics
    periodReturns: FetchMetrics
    twr: FetchMetrics
    risk: FetchMetrics
    prevDayVar: FetchMetrics
    init: DashboardInitMetrics
  }
  _feeAnalyticsLastFetchedAt?: Record<string, number>
  _feeAnalyticsInFlight?: Map<string, Promise<TransactionFeeAnalytics>>
  _feeAnalyticsCache?: Record<string, TransactionFeeAnalytics>
}
export interface TransactionFeeAnalyticsSummary {
  total_fees: number
  fees_ytd: number
  fees_buy: number
  fees_sell: number
  transaction_count: number
  transaction_count_with_fees: number
  average_fee_per_transaction: number
  average_fee_rate_percent: number
  total_volume: number
}
export interface TransactionFeeTimeseriesPoint {
  period: string
  fees_buy: number
  fees_sell: number
  fees_total: number
  cumulative: number
}
export interface TransactionFeeByAsset {
  asset_id: number
  symbol: string
  name: string
  fees_total: number
  fees_buy: number
  fees_sell: number
  volume: number
  fee_rate_percent: number
}
export interface TransactionFeeImpact {
  portfolio_value: number
  total_gain_loss: number
  net_contributions: number
  fees_as_percent_of_portfolio: number | null
  fees_as_percent_of_gains: number | null
  fees_as_percent_of_contributions: number | null
}
export interface TransactionFeeAnalytics {
  summary: TransactionFeeAnalyticsSummary
  timeseries: TransactionFeeTimeseriesPoint[]
  by_asset: TransactionFeeByAsset[]
  impact: TransactionFeeImpact
}
// Axios: interceptors centralisés dans '@/config/axios'
// La gestion des événements de dividendes est maintenant dans portfolioService.ts
export const usePortfolioStore = defineStore('portfolio', {
  state: (): PortfolioState => ({
    portfolios: [] as Portfolio[],
    currentPortfolio: null as Portfolio | null,
    isLoading: false,
    visibilityTypes: [] as VisibilityType[],
    error: null as string | null,
    notFoundPortfolios: new Set<number>(), // Ensemble des IDs de portefeuilles qui n'existent pas
    _portfoliosLastFetchedAt: undefined,
    _portfoliosInFlight: null,
    _portfolioDetailsLastFetchedAt: {},
    _portfolioDetailsInFlight: new Map<number, Promise<Portfolio | null>>(),
    _twrLastFetchedAt: {},
    _twrInFlight: new Map<number, Promise<AllTwrPayload>>(),
    _twrCache: {},
    _periodReturnsLastFetchedAt: {},
    _periodReturnsInFlight: new Map<number, Promise<AllTwrPayload>>(),
    _periodReturnsCache: {},
    _variationsLastFetchedAt: {},
    _variationsInFlight: new Map<string, Promise<PortfolioVariationsResult>>(),
    _riskMetricsLastFetchedAt: {},
    _riskMetricsInFlight: new Map<string, Promise<RiskMetrics>>(),
    _riskMetricsCache: {},
    _prevDayVarLastFetchedAt: {},
    _prevDayVarInFlight: new Map<number, Promise<PreviousDayVariation>>(),
    _perfLastFetchedAt: {},
    _perfInFlight: new Map<string, Promise<PortfolioPerformance>>(),
    _perfCache: {},
    _histPerfLastFetchedAt: {},
    _histPerfInFlight: new Map<string, Promise<HistoricalPerformancePoint[]>>(),
    _firstValuationDateLastFetchedAt: {},
    _firstValuationDateInFlight: new Map<number, Promise<string | null>>(),
    _benchmarkDataCache: {},
    _benchmarkDataInFlight: {},
    dashboardReadyByPortfolio: {},
    _dashboardInitInFlight: new Map<number, Promise<void>>(),
    _feeAnalyticsLastFetchedAt: {},
    _feeAnalyticsInFlight: new Map<string, Promise<TransactionFeeAnalytics>>(),
    _feeAnalyticsCache: {},
    diagnostics: {
      perf: { network_requests: 0, cache_hits: 0, inflight_reuses: 0, errors: 0, last_ms: 0 },
      periodReturns: { network_requests: 0, cache_hits: 0, inflight_reuses: 0, errors: 0, last_ms: 0 },
      twr: { network_requests: 0, cache_hits: 0, inflight_reuses: 0, errors: 0, last_ms: 0 },
      risk: { network_requests: 0, cache_hits: 0, inflight_reuses: 0, errors: 0, last_ms: 0 },
      prevDayVar: { network_requests: 0, cache_hits: 0, inflight_reuses: 0, errors: 0, last_ms: 0 },
      init: { starts: 0, successes: 0, failures: 0, last_ms: 0, last_portfolio: undefined }
    }
  }),
  getters: {
    getPortfolioById: (state) => (id: number) => {
      return state.portfolios.find(p => p.id === id)
    },
    totalPortfolioValue: (state) => {
      return state.portfolios.reduce((total, p) => {
        // Conversion explicite en nombre pour éviter les erreurs de type
        const value = typeof p.total_value === 'string' ? parseFloat(p.total_value) : (p.total_value || 0);
        return total + value;
      }, 0)
    }
  },
  actions: {
    // Réinitialiser entièrement l'état du store (appelé lors du logout)
    resetStore() {
      // Etat simple
      this.portfolios = []
      this.currentPortfolio = null
      this.isLoading = false
      this.visibilityTypes = []
      this.error = null
      this.notFoundPortfolios = new Set<number>()
      // Caches et états internes
      this._portfoliosLastFetchedAt = undefined
      this._portfoliosInFlight = null
      this._portfolioDetailsLastFetchedAt = {}
      this._portfolioDetailsInFlight = new Map<number, Promise<Portfolio | null>>()
      this._twrLastFetchedAt = {}
      this._twrInFlight = new Map<number, Promise<AllTwrPayload>>()
      this._twrCache = {}
      this._periodReturnsLastFetchedAt = {}
      this._periodReturnsInFlight = new Map<number, Promise<AllTwrPayload>>()
      this._periodReturnsCache = {}
      this._variationsLastFetchedAt = {}
      this._variationsInFlight = new Map<string, Promise<PortfolioVariationsResult>>()
      this._riskMetricsLastFetchedAt = {}
      this._riskMetricsInFlight = new Map<string, Promise<RiskMetrics>>()
      this._riskMetricsCache = {}
      this._prevDayVarLastFetchedAt = {}
      this._prevDayVarInFlight = new Map<number, Promise<PreviousDayVariation>>()
      this._perfLastFetchedAt = {}
      this._perfInFlight = new Map<string, Promise<PortfolioPerformance>>()
      this._perfCache = {}
      this._histPerfLastFetchedAt = {}
      this._histPerfInFlight = new Map<string, Promise<HistoricalPerformancePoint[]>>()
      this._firstValuationDateLastFetchedAt = {}
      this._firstValuationDateInFlight = new Map<number, Promise<string | null>>()
      this._benchmarkDataCache = {}
      this._benchmarkDataInFlight = {}
      this.dashboardReadyByPortfolio = {}
      this._dashboardInitInFlight = new Map<number, Promise<void>>()
      this._feeAnalyticsLastFetchedAt = {}
      this._feeAnalyticsInFlight = new Map<string, Promise<TransactionFeeAnalytics>>()
      this._feeAnalyticsCache = {}
      // Diagnostics minimal reset
      this.diagnostics = {
        perf: { network_requests: 0, cache_hits: 0, inflight_reuses: 0, errors: 0, last_ms: 0 },
        periodReturns: { network_requests: 0, cache_hits: 0, inflight_reuses: 0, errors: 0, last_ms: 0 },
        twr: { network_requests: 0, cache_hits: 0, inflight_reuses: 0, errors: 0, last_ms: 0 },
        risk: { network_requests: 0, cache_hits: 0, inflight_reuses: 0, errors: 0, last_ms: 0 },
        prevDayVar: { network_requests: 0, cache_hits: 0, inflight_reuses: 0, errors: 0, last_ms: 0 },
        init: { starts: 0, successes: 0, failures: 0, last_ms: 0, last_portfolio: undefined }
      }
    },
    // Dashboard ready helpers
    setDashboardReady(id: number, ready: boolean) {
      if (!this.dashboardReadyByPortfolio) this.dashboardReadyByPortfolio = {}
      this.dashboardReadyByPortfolio[id] = ready
    },
    isDashboardReady(id: number): boolean {
      return !!this.dashboardReadyByPortfolio?.[id]
    },
    async initDashboardData(id: number, options?: { forceRefresh?: boolean }) {
      try {
        const t0 = Date.now()
        if (this.diagnostics) {
          this.diagnostics.init.starts += 1
          this.diagnostics.init.last_portfolio = id
        }
        if (!this._dashboardInitInFlight) this._dashboardInitInFlight = new Map<number, Promise<void>>()
        const inflight = this._dashboardInitInFlight.get(id)
        if (inflight) return await inflight
        this.setDashboardReady(id, false)
        const req = (async () => {
          // Précharge des données clés du dashboard; en cas d'échec partiel, on continue
          const results = await Promise.allSettled([
            this.fetchPortfolioDetails(id, { forceRefresh: options?.forceRefresh }),
            this.fetchAllPeriodReturns(id, { forceRefresh: options?.forceRefresh }),
            this.fetchPortfolioRiskMetrics(id, { forceRefresh: options?.forceRefresh }),
            this.fetchPortfolioPerformance(id, '1M', { forceRefresh: options?.forceRefresh }),
            this.fetchPortfolioPreviousDayVariation(id, { forceRefresh: options?.forceRefresh }),
            // Précharger les données historiques pour les graphiques (valeur + TWR) en parallèle
            this.fetchPortfolioHistoricalPerformance(id, '6M', false, { forceRefresh: options?.forceRefresh }),
            this.fetchPortfolioHistoricalPerformance(id, '6M', true, { forceRefresh: options?.forceRefresh })
          ])
          if (import.meta.env.DEV) {
            const labels = ['details', 'periodReturns', 'risk', 'perf(1M)', 'prevDayVar', 'histPerf(6M)', 'histPerfTWR(6M)']
            results.forEach((r, idx) => {
              if (r.status === 'rejected') {
                logger.error(`[initDashboardData] Échec du chargement ${labels[idx]} pour portfolio=${id}`, r.reason)
              }
            })
          }
          // Quoi qu'il arrive, lever le drapeau ready pour éviter de bloquer l'UI; chaque composant gère son état/erreur
          this.setDashboardReady(id, true)
        })()
        this._dashboardInitInFlight.set(id, req)
        await req
        if (this.diagnostics) {
          this.diagnostics.init.successes += 1
          this.diagnostics.init.last_ms = Date.now() - t0
        }
      } catch (e) {
        if (import.meta.env.DEV) {
          logger.error('Error during initDashboardData:', e)
        }
        if (this.diagnostics) {
          this.diagnostics.init.failures += 1
          this.diagnostics.init.last_ms = (this.diagnostics.init.last_ms ?? 0)
        }
        // do not throw to avoid blocking UI; keep ready false
      } finally {
        this._dashboardInitInFlight?.delete(id)
      }
    },
    async fetchAllPeriodReturns(id: number, options?: { forceRefresh?: boolean; ttlMs?: number }): Promise<{
      ytd_performance: number,
      mtd_performance: number,
      wtd_performance: number,
      ytd_start_date?: string,
      ytd_end_date?: string,
      mtd_start_date?: string,
      mtd_end_date?: string,
      wtd_start_date?: string,
      wtd_end_date?: string,
      reference_date?: string,
      ytd_initial_value?: number,
      ytd_final_value?: number,
      mtd_initial_value?: number,
      mtd_final_value?: number,
      wtd_initial_value?: number,
      wtd_final_value?: number,
      d1_performance?: number,
      d1_start_date?: string,
      d1_end_date?: string,
      d1_initial_value?: number,
      d1_final_value?: number,
    }> {
      // Mappe l'alias propre HPR vers la même forme aplatie que TWR
      try {
        const t0 = Date.now()
        const forceRefresh = options?.forceRefresh === true
        const ttlMs = options?.ttlMs ?? 30000
        const lastFetched = (this._periodReturnsLastFetchedAt?.[id] ?? this._twrLastFetchedAt?.[id])
        const now = Date.now()
        if (!forceRefresh && lastFetched && (now - lastFetched) < ttlMs) {
          const cached = (this._periodReturnsCache?.[id] ?? this._twrCache?.[id])
          if (cached) {
            if (this.diagnostics) {
              this.diagnostics.twr.cache_hits += 1
              this.diagnostics.periodReturns.cache_hits += 1
            }
            return cached
          }
          if (!this._periodReturnsInFlight) this._periodReturnsInFlight = new Map<number, Promise<AllTwrPayload>>()
          if (!this._twrInFlight) this._twrInFlight = new Map<number, Promise<AllTwrPayload>>()
          const inflight = (this._periodReturnsInFlight.get(id) ?? this._twrInFlight.get(id))
          if (inflight) {
            if (this.diagnostics) {
              this.diagnostics.twr.inflight_reuses += 1
              this.diagnostics.periodReturns.inflight_reuses += 1
            }
            return await inflight
          }
        }
        if (!this._periodReturnsInFlight) this._periodReturnsInFlight = new Map<number, Promise<AllTwrPayload>>()
        if (!this._twrInFlight) this._twrInFlight = new Map<number, Promise<AllTwrPayload>>()
        const inflight = (this._periodReturnsInFlight.get(id) ?? this._twrInFlight.get(id))
        if (inflight) {
          if (this.diagnostics) {
            this.diagnostics.twr.inflight_reuses += 1
            this.diagnostics.periodReturns.inflight_reuses += 1
          }
          return await inflight
        }
        const isoToFr = (iso?: string | null) => {
          if (!iso) return undefined
          const d = new Date(iso)
          if (isNaN(d.getTime())) return undefined
          const dd = String(d.getDate()).padStart(2, '0')
          const mm = String(d.getMonth() + 1).padStart(2, '0')
          const yyyy = d.getFullYear()
          return `${dd}/${mm}/${yyyy}`
        }
        const req = axios.get(`/portfolios/${id}/all_period_returns/`, {
          params: forceRefresh ? { _: Date.now() } : undefined,
          headers: { 'Cache-Control': 'no-cache' }
        }).then((response) => {
          const data = response.data as AllPeriodReturnsApiResponse
          if (!this._periodReturnsLastFetchedAt) this._periodReturnsLastFetchedAt = {}
          if (!this._twrLastFetchedAt) this._twrLastFetchedAt = {}
          this._periodReturnsLastFetchedAt[id] = Date.now()
          this._twrLastFetchedAt[id] = this._periodReturnsLastFetchedAt[id]
          const payload = {
            ytd_performance: data?.ytd?.return ?? 0,
            mtd_performance: data?.mtd?.return ?? 0,
            wtd_performance: data?.wtd?.return ?? 0,
            ytd_start_date: isoToFr(data?.ytd?.start),
            ytd_end_date: isoToFr(data?.ytd?.end),
            mtd_start_date: isoToFr(data?.mtd?.start),
            mtd_end_date: isoToFr(data?.mtd?.end),
            wtd_start_date: isoToFr(data?.wtd?.start),
            wtd_end_date: isoToFr(data?.wtd?.end),
            reference_date: isoToFr(data?.as_of),
            ytd_initial_value: data?.ytd?.start_value ?? undefined,
            ytd_final_value: data?.ytd?.end_value ?? undefined,
            mtd_initial_value: data?.mtd?.start_value ?? undefined,
            mtd_final_value: data?.mtd?.end_value ?? undefined,
            wtd_initial_value: data?.wtd?.start_value ?? undefined,
            wtd_final_value: data?.wtd?.end_value ?? undefined,
            // D-1
            d1_performance: data?.d1?.return ?? undefined,
            d1_start_date: isoToFr(data?.d1?.start),
            d1_end_date: isoToFr(data?.d1?.end),
            d1_initial_value: data?.d1?.start_value ?? undefined,
            d1_final_value: data?.d1?.end_value ?? undefined,
            d1_method: (data?.d1?.method ?? null),
          }
          if (!this._periodReturnsCache) this._periodReturnsCache = {}
          if (!this._twrCache) this._twrCache = {}
          this._periodReturnsCache[id] = payload
          this._twrCache[id] = payload
          return payload
        }).catch(async () => {
          // Fallback: utiliser l'ancien endpoint si l'alias n'existe pas
          return await this.fetchAllTWRPerformances(id, options)
        }).finally(() => {
          this._periodReturnsInFlight?.delete(id)
          this._twrInFlight?.delete(id)
          if (this.diagnostics) {
            const dt = Date.now() - t0
            this.diagnostics.twr.network_requests += 1
            this.diagnostics.twr.last_ms = dt
            this.diagnostics.periodReturns.network_requests += 1
            this.diagnostics.periodReturns.last_ms = dt
          }
        })
        this._periodReturnsInFlight.set(id, req)
        this._twrInFlight.set(id, req)
        return await req
      } catch (error: unknown) {
        if (import.meta.env.DEV) logger.error('Error fetching Period Returns:', error)
        if (this.diagnostics) {
          this.diagnostics.twr.errors += 1
          this.diagnostics.periodReturns.errors += 1
        }
        throw error
      }
    },
    // Cached portfolio performance getter
    getCachedPortfolioPerformance(id: number, period: string, ttlMs: number = 30000): PortfolioPerformance | null {
      const key = `${id}|${period}`
      const last = this._perfLastFetchedAt?.[key]
      const now = Date.now()
      if (last && (now - last) < ttlMs) {
        return this._perfCache?.[key] ?? null
      }
      return null
    },
    // Read risk metrics from cache if still fresh
    getCachedRiskMetrics(id: number, opts?: { alpha?: number; method?: 'historical' | 'gaussian' | 'cornish_fisher'; period?: string }, ttlMs: number = 60000) {
      const key = `${id}|${opts?.alpha ?? ''}|${opts?.method ?? ''}|${opts?.period ?? ''}`
      const lastFetched = this._riskMetricsLastFetchedAt?.[key]
      const now = Date.now()
      if (lastFetched && (now - lastFetched) < ttlMs) {
        return this._riskMetricsCache?.[key] ?? null
      }
      return null
    },
    // Centralized historical performance fetch with cache/dedup
    async fetchPortfolioHistoricalPerformance(
      id: number,
      period: string,
      useTwr: boolean = false,
      options?: { forceRefresh?: boolean; ttlMs?: number; startDate?: string | null }
    ): Promise<HistoricalPerformancePoint[]> {
      const startDate = options?.startDate
      const key = `${id}|${period}|${useTwr ? 'twr' : 'abs'}|${startDate || 'default'}`
      const forceRefresh = options?.forceRefresh === true
      const ttlMs = options?.ttlMs ?? 180000
      const now = Date.now()
      const last = this._histPerfLastFetchedAt?.[key]
      // Return inflight if exists
      if (!forceRefresh && this._histPerfInFlight?.has(key)) {
        return await (this._histPerfInFlight!.get(key) as Promise<HistoricalPerformancePoint[]>)
      }
      // Fresh enough: still allow reuse of inflight if any, else proceed without refetching cache storage (we don't persist payload here)
      if (!forceRefresh && last && (now - last) < ttlMs) {
        const inflight = this._histPerfInFlight?.get(key)
        if (inflight) return await inflight
      }
      if (!this._histPerfInFlight) this._histPerfInFlight = new Map()
      const params: Record<string, string | number | boolean> = { period, use_twr: useTwr }
      if (startDate) {
        params.start_date = startDate
      }
      const req = axios.get(`/portfolios/${id}/historical-performance/`, {
        params
      }).then((response) => {
        if (!this._histPerfLastFetchedAt) this._histPerfLastFetchedAt = {}
        this._histPerfLastFetchedAt[key] = Date.now()
        return response.data as HistoricalPerformancePoint[]
      }).finally(() => {
        this._histPerfInFlight?.delete(key)
      })
      this._histPerfInFlight.set(key, req)
      return await req
    },
    // First valuation date with cache/dedup and fallback to 5Y historical-performance
    async fetchFirstValuationDate(
      id: number,
      options?: { forceRefresh?: boolean; ttlMs?: number }
    ): Promise<string | null> {
      const forceRefresh = options?.forceRefresh === true
      const ttlMs = options?.ttlMs ?? 600000 // 10 minutes
      const last = this._firstValuationDateLastFetchedAt?.[id]
      const now = Date.now()
      if (!forceRefresh && this._firstValuationDateInFlight?.has(id)) {
        return await (this._firstValuationDateInFlight!.get(id) as Promise<string | null>)
      }
      if (!forceRefresh && last && (now - last) < ttlMs) {
        const inflight = this._firstValuationDateInFlight?.get(id)
        if (inflight) return await inflight
      }
      if (!this._firstValuationDateInFlight) this._firstValuationDateInFlight = new Map()
      const req = axios.get(`/portfolios/${id}/first-valuation-date/`).then(async (response) => {
        if (!this._firstValuationDateLastFetchedAt) this._firstValuationDateLastFetchedAt = {}
        this._firstValuationDateLastFetchedAt[id] = Date.now()
        const date = response?.data?.date ?? null
        if (date) return date as string
        // Fallback
        const hist = await this.fetchPortfolioHistoricalPerformance(id, '5Y', false, { forceRefresh: true })
        if (Array.isArray(hist) && hist.length > 0) {
          const sorted = [...hist].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
          return sorted[0]?.date ?? null
        }
        return null
      }).catch(async () => {
        // Fallback on error
        try {
          const hist = await this.fetchPortfolioHistoricalPerformance(id, '5Y', false, { forceRefresh: true })
          if (Array.isArray(hist) && hist.length > 0) {
            const sorted = [...hist].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
            return sorted[0]?.date ?? null
          }
          return null
        } catch {
          return null
        }
      }).finally(() => {
        this._firstValuationDateInFlight?.delete(id)
      })
      this._firstValuationDateInFlight.set(id, req)
      return await req
    },
    // Fetch annualized performance (1Y, 3Y, 5Y) using real TWR values
    async fetchAnnualizedPerformance(
      id: number,
      options?: { forceRefresh?: boolean; ttlMs?: number }
    ): Promise<{
      portfolio_1y?: number
      portfolio_3y?: number
      portfolio_5y?: number
    }> {
      const key = `${id}|annualized-performance`
      const forceRefresh = options?.forceRefresh === true
      const ttlMs = options?.ttlMs ?? 60000 // 1 minute cache
      const now = Date.now()
      // Simple cache check
      if (!forceRefresh && this._histPerfLastFetchedAt?.[key] && (now - this._histPerfLastFetchedAt[key]) < ttlMs) {
        // Return cached data if available (we don't store it, so just skip cache for now)
      }
      const response = await axios.get(`/portfolios/${id}/annualized-performance/`)
      if (!this._histPerfLastFetchedAt) this._histPerfLastFetchedAt = {}
      this._histPerfLastFetchedAt[key] = Date.now()
      return response.data
    },
    // Fetch benchmark data (SPY and CAC comparison)
    async fetchBenchmarkData(
      id: number,
      period: string,
      options?: { forceRefresh?: boolean; ttlMs?: number }
    ): Promise<{
      portfolio_data: Array<{ date: string; value: number }>
      benchmark_data: Array<{ date: string; value: number }>
      cac_data: Array<{ date: string; value: number }>
      urth_data: Array<{ date: string; value: number }>
      qqq_data: Array<{ date: string; value: number }>
      alpha: number
      period: string
    }> {
      const key = `${id}|benchmark|${period}`
      const forceRefresh = options?.forceRefresh === true
      const ttlMs = options?.ttlMs ?? 5 * 60 * 1000 // 5 minutes cache
      const now = Date.now()
      // Vérifier le cache
      if (!forceRefresh && this._benchmarkDataCache?.[key]) {
        const cached = this._benchmarkDataCache[key]
        if (cached.timestamp && (now - cached.timestamp) < ttlMs) {
          return cached.data
        }
      }
      // Vérifier si une requête est déjà en cours
      if (this._benchmarkDataInFlight?.[key]) {
        return this._benchmarkDataInFlight[key]
      }
      // Créer la promesse de requête
      const promise = axios.get(`/portfolios/${id}/benchmark-data/`, {
        params: { period },
        timeout: 30000 // 30 seconds timeout
      }).then(response => {
        // Mettre en cache
        if (!this._benchmarkDataCache) this._benchmarkDataCache = {}
        this._benchmarkDataCache[key] = {
          data: response.data,
          timestamp: Date.now()
        }
        // Nettoyer l'in-flight
        if (this._benchmarkDataInFlight) {
          delete this._benchmarkDataInFlight[key]
        }
        return response.data
      }).catch((error: unknown) => {
        // Nettoyer l'in-flight en cas d'erreur
        if (this._benchmarkDataInFlight) {
          delete this._benchmarkDataInFlight[key]
        }
        // Invalider le cache en cas d'erreur pour permettre un nouveau fetch
        if (this._benchmarkDataCache?.[key]) {
          delete this._benchmarkDataCache[key]
        }
        // Améliorer le message d'erreur
        const apiData = isAxiosError(error)
          ? (error.response?.data as { error?: string; detail?: string } | undefined)
          : undefined
        const errorMessage = apiData?.error || apiData?.detail || (error instanceof Error ? error.message : 'Erreur réseau')
        throw new Error(`Benchmark ${period}: ${errorMessage}`)
      })
      // Stocker la promesse en cours
      if (!this._benchmarkDataInFlight) this._benchmarkDataInFlight = {}
      this._benchmarkDataInFlight[key] = promise
      return promise
    },
    // Fetch benchmark drawdown data
    async fetchBenchmarkDrawdown(
      id: number,
      period: string,
      options?: { forceRefresh?: boolean; ttlMs?: number }
    ): Promise<{
      portfolio_data: Array<{ date: string; drawdown: number }>
      benchmark_data: Array<{ date: string; drawdown: number }>
      cac_data: Array<{ date: string; drawdown: number }>
      period: string
    }> {
      const key = `${id}|benchmark-drawdown|${period}`
      const forceRefresh = options?.forceRefresh === true
      const ttlMs = options?.ttlMs ?? 60000 // 1 minute cache
      const now = Date.now()
      if (!forceRefresh && this._histPerfLastFetchedAt?.[key] && (now - this._histPerfLastFetchedAt[key]) < ttlMs) {
        // Return cached data if available (we don't store it, so just skip cache for now)
      }
      const response = await axios.get(`/portfolios/${id}/benchmark-drawdown/`, {
        params: { period }
      })
      if (!this._histPerfLastFetchedAt) this._histPerfLastFetchedAt = {}
      this._histPerfLastFetchedAt[key] = Date.now()
      return response.data
    },
    // Fetch benchmark volatility data
    async fetchBenchmarkVolatility(
      id: number,
      period: string,
      options?: { forceRefresh?: boolean; ttlMs?: number }
    ): Promise<{
      portfolio_data: Array<{ date: string; volatility: number }>
      benchmark_data: Array<{ date: string; volatility: number }>
      cac_data: Array<{ date: string; volatility: number }>
      period: string
    }> {
      const key = `${id}|benchmark-volatility|${period}`
      const forceRefresh = options?.forceRefresh === true
      const ttlMs = options?.ttlMs ?? 60000 // 1 minute cache
      const now = Date.now()
      if (!forceRefresh && this._histPerfLastFetchedAt?.[key] && (now - this._histPerfLastFetchedAt[key]) < ttlMs) {
        // Return cached data if available (we don't store it, so just skip cache for now)
      }
      const response = await axios.get(`/portfolios/${id}/benchmark-volatility/`, {
        params: { period }
      })
      if (!this._histPerfLastFetchedAt) this._histPerfLastFetchedAt = {}
      this._histPerfLastFetchedAt[key] = Date.now()
      return response.data
    },
    // Méthode pour nettoyer les données d'un portefeuille supprimé
    cleanupPortfolioData(portfolioId: number) {
      // Supprimer le portefeuille de la liste
      this.portfolios = this.portfolios.filter(p => p.id !== portfolioId)
      // Si c'est le portefeuille courant, le réinitialiser
      if (this.currentPortfolio?.id === portfolioId) {
        this.currentPortfolio = null
      }
      // Ajouter l'ID à la liste des portefeuilles inexistants
      this.notFoundPortfolios.add(portfolioId)
      // Invalider la sélection persistée si elle pointe vers le portefeuille supprimé
      try {
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const key = localStorage.key(i)
          if (key && (key === 'selectedPortfolioId' || key.startsWith('selectedPortfolioId_'))) {
            if (localStorage.getItem(key) === String(portfolioId)) {
              localStorage.removeItem(key)
            }
          }
        }
      } catch {
        // Ignorer les erreurs localStorage
      }
      // Rediriger vers la liste des portefeuilles si nécessaire
      try {
        if (router && router.currentRoute.value.params.id === portfolioId.toString()) {
          router.push('/portfolios')
        }
      } catch {
        // Ignorer les erreurs de redirection (peut arriver dans certains contextes)
      }
    },
    async fetchPortfolios(options?: { forceRefresh?: boolean; ttlMs?: number }) {
      try {
        const forceRefresh = options?.forceRefresh === true
        const ttlMs = options?.ttlMs ?? 120000 // 2 minutes par défaut (optimisé)
        // Si une requête est déjà en cours, la réutiliser
        if (this._portfoliosInFlight) {
          await this._portfoliosInFlight
          return
        }
        // Si les données sont fraîches et qu'on ne force pas, ne pas refetch
        const now = Date.now()
        if (!forceRefresh && this._portfoliosLastFetchedAt && (now - this._portfoliosLastFetchedAt) < ttlMs) {
          return
        }
        this.isLoading = true
        this._portfoliosInFlight = axios.get('/portfolios/').then((response) => {
          this.portfolios = response.data
          this.error = null
          this._portfoliosLastFetchedAt = Date.now()
        }).finally(() => {
          this._portfoliosInFlight = null
        })
        await this._portfoliosInFlight
      } catch (error: unknown) {
        this.error = isAxiosError(error)
          ? ((error.response?.data as { detail?: string } | undefined)?.detail || 'Erreur lors du chargement des portefeuilles')
          : 'Erreur lors du chargement des portefeuilles'
        if (import.meta.env.DEV) {
          logger.error('Error fetching portfolios:', error)
        }
        throw error
      } finally {
        this.isLoading = false
      }
    },
    fetchPortfolioDetails: async function (id: number, options?: { forceRefresh?: boolean; ttlMs?: number }): Promise<Portfolio | null> {
      try {
        // Vérifier si l'ID est valide
        if (!id) {
          // Aucun ID de portefeuille fourni, impossible de récupérer les détails
          return null
        }
        // Vérifier si ce portefeuille a déjà été identifié comme inexistant
        if (this.notFoundPortfolios.has(id)) {
          this.error = `Le portefeuille avec l'ID ${id} n'existe pas ou a été supprimé`
          throw new Error(`Portefeuille avec l'ID ${id} non trouvé`)
        }
        const forceRefresh = options?.forceRefresh === true
        const ttlMs = options?.ttlMs ?? 60000 // 1 minute par défaut (optimisé)
        // Si une requête pour ce portefeuille est déjà en cours, la réutiliser
        if (!this._portfolioDetailsInFlight) {
          this._portfolioDetailsInFlight = new Map<number, Promise<Portfolio | null>>()
        }
        const inflight = this._portfolioDetailsInFlight.get(id)
        if (inflight) {
          return await inflight
        }
        // Si les données actuelles sont fraîches et qu'on ne force pas, retourner le cache
        const lastFetched = this._portfolioDetailsLastFetchedAt?.[id]
        const now = Date.now()
        if (!forceRefresh && lastFetched && (now - lastFetched) < ttlMs) {
          // Récupérer le portefeuille en cache (liste ou courant)
          const cached = this.portfolios.find(p => p.id === id) || (this.currentPortfolio?.id === id ? this.currentPortfolio : null)
          if (cached) {
            // Mettre à jour le portefeuille courant pour refléter la sélection de l'utilisateur
            this.currentPortfolio = cached
            // Mettre à jour le timestamp de fraîcheur
            if (!this._portfolioDetailsLastFetchedAt) {
              this._portfolioDetailsLastFetchedAt = {}
            }
            this._portfolioDetailsLastFetchedAt[id] = now
            return cached
          }
        }
        // Sauvegarder les données de dividendes avant de recharger le portefeuille
        let savedTotalDividends = null;
        // Vérifier si le portefeuille courant existe et s'il a des dividendes
        if (this.currentPortfolio?.id === id && this.currentPortfolio.total_dividends !== undefined) {
          savedTotalDividends = this.currentPortfolio.total_dividends
        } else {
          const portfolioInList = this.portfolios.find(p => p.id === id)
          if (portfolioInList?.total_dividends !== undefined) savedTotalDividends = portfolioInList.total_dividends
        }
        this.isLoading = true
        // Utiliser l'intercepteur global d'Axios plutôt que d'ajouter manuellement le token
        // Forcer le chargement des latest_values pour s'assurer que les dates sont disponibles
        const requestPromise = axios.get(`/portfolios/${id}/?with_latest_values=true`).then((response) => {
          // Restaurer les données de dividendes si elles existaient précédemment
          if (savedTotalDividends !== null) {
            response.data.total_dividends = savedTotalDividends;
          }
          // Filtrer les actifs inactifs pour l'affichage et ajouter le champ last_update_date
          if (response.data && response.data.assets && Array.isArray(response.data.assets)) {
            // Filtrer pour ne garder que les actifs actifs (is_active === true)
            // Les valeurs total_gain_loss et total_gain_loss_percentage viennent du backend
            // qui calcule déjà correctement en excluant les actifs inactifs
            const activeAssets = response.data.assets.filter((asset: PortfolioAsset) => asset.is_active !== false);
            // Appliquer le filtrage et ajouter last_update_date
            response.data.assets = activeAssets.map((asset: PortfolioAsset) => {
              // Récupérer la dernière valeur d'actif si disponible
              const latestValue = asset.asset?.latest_values?.[0] || null;
              // Déterminer la date de dernière mise à jour avec fallback
              let lastUpdateDate = null;
              if (latestValue && latestValue.date) {
                lastUpdateDate = latestValue.date;
              } else if (asset.asset?.updated_at) {
                // Fallback sur updated_at de l'actif si latest_values n'est pas disponible
                lastUpdateDate = asset.asset.updated_at;
              } else if (asset.updated_at) {
                // Fallback sur updated_at de l'actif du portefeuille
                lastUpdateDate = asset.updated_at;
              }
              // Ajouter la date de dernière mise à jour
              return {
                ...asset,
                last_update_date: lastUpdateDate
              };
            });
          }
          this.currentPortfolio = response.data
          // Mettre également à jour le portefeuille dans la liste des portefeuilles
          const portfolioIndex = this.portfolios.findIndex(p => p.id === id);
          if (portfolioIndex !== -1) {
            // Préserver les dividendes dans la liste des portefeuilles également
            this.portfolios[portfolioIndex] = {
              ...this.portfolios[portfolioIndex],
              ...response.data,
              total_dividends: savedTotalDividends !== null ? savedTotalDividends : response.data.total_dividends || 0
            };
          }
          this.error = null;
          // Ensure last-fetched map exists before writing
          if (!this._portfolioDetailsLastFetchedAt) {
            this._portfolioDetailsLastFetchedAt = {}
          }
          this._portfolioDetailsLastFetchedAt[id] = Date.now()
          return response.data
        })
        // Enregistrer la requête en cours pour ce portefeuille
        this._portfolioDetailsInFlight.set(id, requestPromise)
        const result = await requestPromise
        return result
      } catch (error: unknown) {
        if (isAxiosError(error) && error.response?.status === 404) {
          // Marquer ce portefeuille comme inexistant
          this.notFoundPortfolios.add(id)
          this.error = `Le portefeuille avec l'ID ${id} n'existe pas ou a été supprimé`
          // Nettoyer les données de ce portefeuille
          this.cleanupPortfolioData(id)
        } else {
          this.error = isAxiosError(error)
            ? ((error.response?.data as { detail?: string } | undefined)?.detail || 'Erreur lors du chargement des détails du portefeuille')
            : 'Erreur lors du chargement des détails du portefeuille'
        }
        if (import.meta.env.DEV) {
          logger.error('Error fetching portfolio details:', error)
        }
        throw error
      } finally {
        // Nettoyer l'état in-flight pour ce portefeuille
        if (this._portfolioDetailsInFlight) {
          this._portfolioDetailsInFlight.delete(id)
        }
        this.isLoading = false
      }
      },
    createPortfolio: async function (portfolio: Partial<Portfolio>): Promise<Portfolio> {
      try {
        this.isLoading = true
        const response = await axios.post('/portfolios/', portfolio)
        this.portfolios.push(response.data)
        this.currentPortfolio = response.data
        this.error = null
        return response.data
      } catch (error: unknown) {
        this.error = 'Erreur lors de la création du portefeuille'
        if (import.meta.env.DEV) {
          logger.error('Error creating portfolio:', error)
        }
        throw error
      } finally {
        this.isLoading = false
      }
    },
    updatePortfolio: async function (id: number, portfolio: Partial<Portfolio>): Promise<Portfolio> {
      try {
        this.isLoading = true
        const response = await axios.put(`/portfolios/${id}/`, portfolio)
        const index = this.portfolios.findIndex(p => p.id === id)
        if (index !== -1) {
          this.portfolios[index] = response.data
        }
        this.error = null
        return response.data
      } catch (error: unknown) {
        this.error = 'Erreur lors de la mise à jour du portefeuille'
        if (import.meta.env.DEV) {
          logger.error('Error updating portfolio:', error)
        }
        throw error
      } finally {
        this.isLoading = false
      }
    },
    deletePortfolio: async function (id: number): Promise<{ success: boolean, message: string }> {
      try {
        this.isLoading = true
        // Mémoriser avant cleanup (qui remet currentPortfolio à null)
        const wasCurrent = this.currentPortfolio?.id === id
        await axios.delete(`/portfolios/${id}/`)
        this.cleanupPortfolioData(id)
        if (wasCurrent) {
          if (this.portfolios.length > 0) {
            const nextPortfolio =
              this.portfolios.find(p => p.is_default) || this.portfolios[0]
            await this.fetchPortfolioDetails(nextPortfolio.id)
          } else {
            this.currentPortfolio = null
          }
        }
        this.error = null
        return { success: true, message: 'Portefeuille supprimé avec succès' }
      } catch (error: unknown) {
        this.error = 'Erreur lors de la suppression du portefeuille'
        if (import.meta.env.DEV) {
          logger.error('Error deleting portfolio:', error)
        }
        return { success: false, message: 'Erreur lors de la suppression du portefeuille' }
      } finally {
        this.isLoading = false
      }
    },
    addAssetToPortfolio: async function (portfolioId: number, asset: { asset_id: number, quantity: number, purchase_value: number }): Promise<PortfolioAsset> {
      try {
        this.isLoading = true
        const response = await axios.post('/portfolio-assets/', {
          portfolio: portfolioId,
          ...asset
        })
        await this.fetchPortfolioDetails(portfolioId)
        this.error = null
        return response.data
      } catch (error: unknown) {
        this.error = 'Erreur lors de l\'ajout de l\'actif au portefeuille'
        if (import.meta.env.DEV) {
          logger.error('Error adding asset to portfolio:', error)
        }
        throw error
      } finally {
        this.isLoading = false
      }
    },
    updatePortfolioAsset: async function (assetId: number, updates: { quantity?: number, purchase_value?: number }): Promise<PortfolioAsset> {
      try {
        this.isLoading = true
        const response = await axios.patch(`/portfolio-assets/${assetId}/`, updates)
        if (this.currentPortfolio) {
          await this.fetchPortfolioDetails(this.currentPortfolio.id)
        }
        this.error = null
        return response.data
      } catch (error: unknown) {
        this.error = 'Erreur lors de la mise à jour de l\'actif'
        if (import.meta.env.DEV) {
          logger.error('Error updating portfolio asset:', error)
        }
        throw error
      } finally {
        this.isLoading = false
      }
    },
    deletePortfolioAsset: async function (assetId: number): Promise<void> {
      try {
        this.isLoading = true;
        // Appel API pour marquer l'actif comme inactif (suppression logique)
        await axios.delete(`/portfolio-assets/${assetId}/`);
        // Rafraîchir les données du portefeuille pour refléter les changements
        if (this.currentPortfolio) {
          await this.fetchPortfolioDetails(this.currentPortfolio.id);
        }
        this.error = null;
      } catch (error: unknown) {
        this.error = 'Erreur lors de la suppression de l\'actif';
        // Log détaillé de l'erreur
        logger.error(`Échec de la suppression de l'actif #${assetId}`, {
          message: error instanceof Error ? error.message : String(error),
          status: isAxiosError(error) ? error.response?.status : undefined,
          data: isAxiosError(error) ? error.response?.data : undefined
        });
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
    async fetchPortfolioPerformance(id: number, period: string = '1M', options?: { forceRefresh?: boolean; ttlMs?: number }): Promise<PortfolioPerformance> {
      const t0 = Date.now()
      const forceRefresh = options?.forceRefresh === true
      const ttlMs = options?.ttlMs ?? 30000
      // Try requested period, then fall back to 3M and YTD
      const candidates = Array.from(new Set([period, '3M', 'YTD']))
      const tryOnce = async (p: string): Promise<PortfolioPerformance | null> => {
        const key = `${id}|${p}`
        const now = Date.now()
        const last = this._perfLastFetchedAt?.[key]
        // Fresh cache
        if (!forceRefresh && last && (now - last) < ttlMs) {
          const cached = this._perfCache?.[key]
          if (cached) {
            if (this.diagnostics) this.diagnostics.perf.cache_hits += 1
            return cached
          }
          if (!this._perfInFlight) this._perfInFlight = new Map<string, Promise<PortfolioPerformance>>()
          const inflightCached = this._perfInFlight.get(key)
          if (inflightCached) {
            if (this.diagnostics) this.diagnostics.perf.inflight_reuses += 1
            return await inflightCached
          }
        }
        if (!this._perfInFlight) this._perfInFlight = new Map<string, Promise<PortfolioPerformance>>()
        const inflight = this._perfInFlight.get(key)
        if (inflight) {
          if (this.diagnostics) this.diagnostics.perf.inflight_reuses += 1
          return await inflight
        }
        const req = axios.get(`/portfolios/${id}/asset_variations/`, { params: { period: p } })
          .then((response) => {
            if (!this._perfLastFetchedAt) this._perfLastFetchedAt = {}
            if (!this._perfCache) this._perfCache = {}
            this._perfLastFetchedAt[key] = Date.now()
            this._perfCache[key] = response.data
            this.error = null
            return response.data as PortfolioPerformance
          })
          .catch((err) => {
            // Do not set global error yet; allow fallback
            if (import.meta.env.DEV) {
              logger.error(`[perf] Échec pour period=${p} portfolio=${id}`, err)
            }
            return null
          })
          .finally(() => {
            this._perfInFlight?.delete(key)
            if (this.diagnostics) {
              this.diagnostics.perf.network_requests += 1
              this.diagnostics.perf.last_ms = Date.now() - t0
            }
          })
        this._perfInFlight.set(key, req as unknown as Promise<PortfolioPerformance>)
        return await req
      }
      for (const p of candidates) {
        const result = await tryOnce(p)
        if (result) {
          return result
        }
      }
      // All attempts failed
      this.error = 'Erreur lors du chargement des performances'
      if (this.diagnostics) this.diagnostics.perf.errors += 1
      throw new Error('fetchPortfolioPerformance failed for all candidate periods')
    },
    async fetchAllTWRPerformances(id: number, options?: { forceRefresh?: boolean; ttlMs?: number }): Promise<{ 
      ytd_performance: number, 
      mtd_performance: number, 
      wtd_performance: number,
      ytd_start_date?: string,
      mtd_start_date?: string,
      wtd_start_date?: string,
      reference_date?: string,
      ytd_initial_value?: number,
      ytd_final_value?: number,
      mtd_initial_value?: number,
      mtd_final_value?: number,
      wtd_initial_value?: number,
      wtd_final_value?: number
    }> {
      try {
        const t0 = Date.now()
        const forceRefresh = options?.forceRefresh === true
        const ttlMs = options?.ttlMs ?? 30000
        const lastFetched = this._twrLastFetchedAt?.[id]
        const now = Date.now()
        if (!forceRefresh && lastFetched && (now - lastFetched) < ttlMs) {
          // Si un payload est en cache et toujours frais, le servir immédiatement
          const cached = this._twrCache?.[id]
          if (cached) {
            if (this.diagnostics) this.diagnostics.twr.cache_hits += 1
            return cached
          }
          // Sinon réutiliser une requête en vol si présente
          if (!this._twrInFlight) {
            this._twrInFlight = new Map<number, Promise<AllTwrPayload>>()
          }
          const inflight = this._twrInFlight.get(id)
          if (inflight) {
            if (this.diagnostics) this.diagnostics.twr.inflight_reuses += 1
            return await inflight
          }
        }
        if (!this._twrInFlight) {
          this._twrInFlight = new Map<number, Promise<AllTwrPayload>>()
        }
        const inflight = this._twrInFlight.get(id)
        if (inflight) {
          if (this.diagnostics) this.diagnostics.twr.inflight_reuses += 1
          return await inflight
        }
        const req = axios.get(`/portfolios/${id}/all_twr_performances/`).then((response) => {
          if (!this._twrLastFetchedAt) {
            this._twrLastFetchedAt = {}
          }
          this._twrLastFetchedAt[id] = Date.now()
          const payload = {
            ytd_performance: response.data.ytd_performance || 0,
            mtd_performance: response.data.mtd_performance || 0,
            wtd_performance: response.data.wtd_performance || 0,
            ytd_start_date: response.data.ytd_start_date,
            mtd_start_date: response.data.mtd_start_date,
            wtd_start_date: response.data.wtd_start_date,
            reference_date: response.data.reference_date,
            ytd_initial_value: response.data.ytd_initial_value,
            ytd_final_value: response.data.ytd_final_value,
            mtd_initial_value: response.data.mtd_initial_value,
            mtd_final_value: response.data.mtd_final_value,
            wtd_initial_value: response.data.wtd_initial_value,
            wtd_final_value: response.data.wtd_final_value
          }
          // Stocker en cache le payload
          if (!this._twrCache) this._twrCache = {}
          this._twrCache[id] = payload
          return payload
        }).finally(() => {
          this._twrInFlight?.delete(id)
          if (this.diagnostics) {
            this.diagnostics.twr.network_requests += 1
            this.diagnostics.twr.last_ms = Date.now() - t0
          }
        })
        this._twrInFlight.set(id, req)
        return await req
      } catch (error: unknown) {
        if (import.meta.env.DEV) {
          logger.error('Error fetching TWR performances:', error)
        }
        if (this.diagnostics) this.diagnostics.twr.errors += 1
        throw error
      }
    },
    async fetchPortfolioDailyVariations(id: number, options?: { forceRefresh?: boolean; ttlMs?: number }): Promise<{ portfolio_name: string, max_asset_daily_change: number, min_asset_daily_change: number }> {
      try {
        this.isLoading = true
        const key = `${id}:daily`
        const forceRefresh = options?.forceRefresh === true
        const ttlMs = options?.ttlMs ?? 30000
        const lastFetched = this._variationsLastFetchedAt?.[key]
        const now = Date.now()
        if (!forceRefresh && lastFetched && (now - lastFetched) < ttlMs) {
          if (!this._variationsInFlight) {
            this._variationsInFlight = new Map<string, Promise<PortfolioVariationsResult>>()
          }
          const inflight = this._variationsInFlight.get(key)
          if (inflight) return await inflight
        }
        if (!this._variationsInFlight) {
          this._variationsInFlight = new Map<string, Promise<PortfolioVariationsResult>>()
        }
        const inflight = this._variationsInFlight.get(key)
        if (inflight) return await inflight
        const req = axios.get(`/portfolios/${id}/daily_variations/`).then((response) => {
          this.error = null
          if (!this._variationsLastFetchedAt) {
            this._variationsLastFetchedAt = {}
          }
          this._variationsLastFetchedAt[key] = Date.now()
          return response.data as { portfolio_name: string, max_asset_daily_change: number, min_asset_daily_change: number }
        }).finally(() => {
          if (this._variationsInFlight) {
            this._variationsInFlight.delete(key)
          }
        })
        this._variationsInFlight.set(key, req)
        return await req
      } catch (error: unknown) {
        this.error = 'Erreur lors du chargement des performances'
        if (import.meta.env.DEV) {
          logger.error('Error fetching portfolio performance:', error)
        }
        throw error
      } finally {
        this.isLoading = false
      }
    },
    async fetchPortfolioWeeklyVariations(id: number, options?: { forceRefresh?: boolean; ttlMs?: number }): Promise<{ portfolio_name: string, max_asset_weekly_change: number, min_asset_weekly_change: number }> {
      try {
        this.isLoading = true
        const key = `${id}:weekly`
        const forceRefresh = options?.forceRefresh === true
        const ttlMs = options?.ttlMs ?? 30000
        const lastFetched = this._variationsLastFetchedAt?.[key]
        const now = Date.now()
        if (!forceRefresh && lastFetched && (now - lastFetched) < ttlMs) {
          if (!this._variationsInFlight) {
            this._variationsInFlight = new Map<string, Promise<PortfolioVariationsResult>>()
          }
          const inflight = this._variationsInFlight.get(key)
          if (inflight) return await inflight
        }
        if (!this._variationsInFlight) {
          this._variationsInFlight = new Map<string, Promise<PortfolioVariationsResult>>()
        }
        const inflight = this._variationsInFlight.get(key)
        if (inflight) return await inflight
        const req = axios.get(`/portfolios/${id}/weekly_variations/`).then((response) => {
          if (!this._variationsLastFetchedAt) {
            this._variationsLastFetchedAt = {}
          }
          this._variationsLastFetchedAt[key] = Date.now()
          return response.data as { portfolio_name: string, max_asset_weekly_change: number, min_asset_weekly_change: number }
        }).finally(() => {
          if (this._variationsInFlight) {
            this._variationsInFlight.delete(key)
          }
        })
        this._variationsInFlight.set(key, req)
        return await req
      } catch (error: unknown) {
        if (import.meta.env.DEV) {
          logger.error('Error fetching weekly variations:', error)
        }
        throw error
      } finally {
        this.isLoading = false
      }
    },
    async fetchPortfolioMonthlyVariations(id: number, options?: { forceRefresh?: boolean; ttlMs?: number }): Promise<{ portfolio_name: string, max_asset_monthly_change: number, min_asset_monthly_change: number }> {
      try {
        this.isLoading = true
        const key = `${id}:monthly`
        const forceRefresh = options?.forceRefresh === true
        const ttlMs = options?.ttlMs ?? 30000
        const lastFetched = this._variationsLastFetchedAt?.[key]
        const now = Date.now()
        if (!forceRefresh && lastFetched && (now - lastFetched) < ttlMs) {
          if (!this._variationsInFlight) {
            this._variationsInFlight = new Map<string, Promise<PortfolioVariationsResult>>()
          }
          const inflight = this._variationsInFlight.get(key)
          if (inflight) return await inflight
        }
        if (!this._variationsInFlight) {
          this._variationsInFlight = new Map<string, Promise<PortfolioVariationsResult>>()
        }
        const inflight = this._variationsInFlight.get(key)
        if (inflight) return await inflight
        const req = axios.get(`/portfolios/${id}/monthly_variations/`).then((response) => {
          if (!this._variationsLastFetchedAt) {
            this._variationsLastFetchedAt = {}
          }
          this._variationsLastFetchedAt[key] = Date.now()
          return response.data as { portfolio_name: string, max_asset_monthly_change: number, min_asset_monthly_change: number }
        }).finally(() => {
          if (this._variationsInFlight) {
            this._variationsInFlight.delete(key)
          }
        })
        this._variationsInFlight.set(key, req)
        return await req
      } catch (error: unknown) {
        if (import.meta.env.DEV) {
          logger.error('Error fetching monthly variations:', error)
        }
        throw error
      } finally {
        this.isLoading = false
      }
    },
    async fetchPortfolioHistoricalVariations(id: number, options?: { forceRefresh?: boolean; ttlMs?: number }): Promise<{ 
      portfolio_name: string, 
      historical_max_change: number, 
      date_of_historical_max: string,
      historical_min_change: number,
      date_of_historical_min: string,
      total_change: number
    }> {
      try {
        this.isLoading = true
        const key = `${id}:historical`
        const forceRefresh = options?.forceRefresh === true
        const ttlMs = options?.ttlMs ?? 30000
        const lastFetched = this._variationsLastFetchedAt?.[key]
        const now = Date.now()
        if (!forceRefresh && lastFetched && (now - lastFetched) < ttlMs) {
          if (!this._variationsInFlight) {
            this._variationsInFlight = new Map<string, Promise<PortfolioVariationsResult>>()
          }
          const inflight = this._variationsInFlight.get(key)
          if (inflight) return await inflight
        }
        if (!this._variationsInFlight) {
          this._variationsInFlight = new Map<string, Promise<PortfolioVariationsResult>>()
        }
        const inflight = this._variationsInFlight.get(key)
        if (inflight) return await inflight
        const req = axios.get(`/portfolios/${id}/historical_variations/`).then((response) => {
          if (!this._variationsLastFetchedAt) {
            this._variationsLastFetchedAt = {}
          }
          this._variationsLastFetchedAt[key] = Date.now()
          return response.data
        }).finally(() => {
          if (this._variationsInFlight) {
            this._variationsInFlight.delete(key)
          }
        })
        this._variationsInFlight.set(key, req)
        return await req
      } catch (error: unknown) {
        if (import.meta.env.DEV) {
          logger.error('Error fetching historical variations:', error)
        }
        throw error
      } finally {
        this.isLoading = false
      }
    },
    async fetchPortfolioYearlyVariations(id: number, options?: { forceRefresh?: boolean; ttlMs?: number }): Promise<{ portfolio_name: string, max_asset_yearly_change: number, min_asset_yearly_change: number, best_year: number | null, worst_year: number | null, years_analyzed: number }> {
      try {
        this.isLoading = true
        const key = `${id}:yearly`
        const forceRefresh = options?.forceRefresh === true
        const ttlMs = options?.ttlMs ?? 30000
        const lastFetched = this._variationsLastFetchedAt?.[key]
        const now = Date.now()
        if (!forceRefresh && lastFetched && (now - lastFetched) < ttlMs) {
          if (!this._variationsInFlight) {
            this._variationsInFlight = new Map<string, Promise<PortfolioVariationsResult>>()
          }
          const inflight = this._variationsInFlight.get(key)
          if (inflight) return await inflight
        }
        if (!this._variationsInFlight) {
          this._variationsInFlight = new Map<string, Promise<PortfolioVariationsResult>>()
        }
        const inflight = this._variationsInFlight.get(key)
        if (inflight) return await inflight
        const req = axios.get(`/portfolios/${id}/yearly_variations/`).then((response) => {
          if (!this._variationsLastFetchedAt) {
            this._variationsLastFetchedAt = {}
          }
          this._variationsLastFetchedAt[key] = Date.now()
          return response.data as { portfolio_name: string, max_asset_yearly_change: number, min_asset_yearly_change: number, best_year: number | null, worst_year: number | null, years_analyzed: number }
        }).finally(() => {
          if (this._variationsInFlight) {
            this._variationsInFlight.delete(key)
          }
        })
        this._variationsInFlight.set(key, req)
        return await req
      } catch (error: unknown) {
        if (import.meta.env.DEV) {
          logger.error('Error fetching yearly variations:', error)
        }
        throw error
      } finally {
        this.isLoading = false
      }
    },
    async fetchPortfolioRiskMetrics(id: number, options?: { forceRefresh?: boolean; ttlMs?: number; alpha?: number; method?: 'historical' | 'gaussian' | 'cornish_fisher'; period?: string }): Promise<{
      sharpe_ratio: number | null,
      sortino_ratio?: number | null,
      volatility: number | null,
      volatility_standard: number | null,
      volatility_conditional: number | null,
      max_drawdown: number | null,
      max_runup?: number | null,
      var?: number | null,
      cvar?: number | null,
      var_95?: number | null,
      cvar_95?: number | null,
      var_99?: number | null,
      cvar_99?: number | null,
      alpha?: number,
      outliers_removed: number | null,
      filtered_data_points: number | null,
      warning?: string
    }> {
      try {
        const t0 = Date.now()
        const forceRefresh = options?.forceRefresh === true
        const ttlMs = options?.ttlMs ?? 60000
        const alpha = options?.alpha
        const method = options?.method
        const period = options?.period
        const key = `${id}|${alpha ?? ''}|${method ?? ''}|${period ?? ''}`
        const lastFetched = this._riskMetricsLastFetchedAt?.[key]
        const now = Date.now()
        // Serve fresh cache payload immediately
        if (!forceRefresh && lastFetched && (now - lastFetched) < ttlMs) {
          const cached = this._riskMetricsCache?.[key]
          if (cached) {
            if (this.diagnostics) this.diagnostics.risk.cache_hits += 1
            return cached
          }
          if (!this._riskMetricsInFlight) {
            this._riskMetricsInFlight = new Map<string, Promise<RiskMetrics>>()
          }
          const inflightCached = this._riskMetricsInFlight.get(key)
          if (inflightCached) {
            if (this.diagnostics) this.diagnostics.risk.inflight_reuses += 1
            return await inflightCached
          }
        }
        if (!this._riskMetricsInFlight) {
          this._riskMetricsInFlight = new Map<string, Promise<RiskMetrics>>()
        }
        const inflight = this._riskMetricsInFlight.get(key)
        if (inflight) {
          if (this.diagnostics) this.diagnostics.risk.inflight_reuses += 1
          return await inflight
        }
        const req = axios.get(`/portfolios/${id}/risk_metrics/`, {
          params: {
            ...(alpha !== undefined ? { alpha } : {}),
            ...(method !== undefined ? { method } : {}),
            ...(period !== undefined ? { period } : {}),
          }
        }).then((response) => {
          if (!this._riskMetricsLastFetchedAt) {
            this._riskMetricsLastFetchedAt = {}
          }
          this._riskMetricsLastFetchedAt[key] = Date.now()
          // Sanitize payload: non-finite numbers -> null; propagate warning
          const d = response.data as RiskMetricsApiResponse
          const toNumOrNull = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)
          const sanitized = {
            sharpe_ratio: toNumOrNull(d?.sharpe_ratio),
            sortino_ratio: toNumOrNull(d?.sortino_ratio),
            annualized_return: toNumOrNull(d?.annualized_return),
            volatility: toNumOrNull(d?.volatility),
            volatility_standard: toNumOrNull(d?.volatility_standard),
            volatility_conditional: toNumOrNull(d?.volatility_conditional),
            max_drawdown: toNumOrNull(d?.max_drawdown),
            max_runup: toNumOrNull(d?.max_runup),
            var: toNumOrNull(d?.var),
            cvar: toNumOrNull(d?.cvar),
            var_95: toNumOrNull(d?.var_95),
            cvar_95: toNumOrNull(d?.cvar_95),
            var_99: toNumOrNull(d?.var_99),
            cvar_99: toNumOrNull(d?.cvar_99),
            alpha: typeof d?.alpha === 'number' && Number.isFinite(d.alpha) ? d.alpha : undefined,
            outliers_removed: toNumOrNull(d?.outliers_removed),
            filtered_data_points: toNumOrNull(d?.filtered_data_points),
            returns_source: typeof d?.returns_source === 'string' ? d.returns_source : undefined,
            warning: typeof d?.warning === 'string' && d.warning.length > 0 ? d.warning : undefined,
          }
          // Save sanitized payload cache
          if (!this._riskMetricsCache) this._riskMetricsCache = {}
          this._riskMetricsCache[key] = sanitized
          return sanitized
        }).finally(() => {
          if (this._riskMetricsInFlight) {
            this._riskMetricsInFlight.delete(key)
          }
          if (this.diagnostics) {
            this.diagnostics.risk.network_requests += 1
            this.diagnostics.risk.last_ms = Date.now() - t0
          }
        })
        this._riskMetricsInFlight.set(key, req)
        return await req
      } catch (error: unknown) {
        if (import.meta.env.DEV) {
          logger.error('Error fetching risk metrics:', error)
        }
        if (this.diagnostics) this.diagnostics.risk.errors += 1
        throw error
      }
    },
    async fetchTransactionFeeAnalytics(
      id: number,
      options?: {
        forceRefresh?: boolean
        ttlMs?: number
        date_from?: string
        date_to?: string
        group_by?: 'month' | 'year'
      }
    ): Promise<TransactionFeeAnalytics> {
      const forceRefresh = options?.forceRefresh === true
      const ttlMs = options?.ttlMs ?? 60000
      const date_from = options?.date_from ?? ''
      const date_to = options?.date_to ?? ''
      const group_by = options?.group_by ?? 'month'
      const key = `${id}|${date_from}|${date_to}|${group_by}`
      const now = Date.now()
      if (!forceRefresh && this._feeAnalyticsLastFetchedAt?.[key]) {
        const age = now - this._feeAnalyticsLastFetchedAt[key]
        if (age < ttlMs) {
          const cached = this._feeAnalyticsCache?.[key]
          if (cached) return cached
        }
      }
      if (!this._feeAnalyticsInFlight) {
        this._feeAnalyticsInFlight = new Map<string, Promise<TransactionFeeAnalytics>>()
      }
      const inflight = this._feeAnalyticsInFlight.get(key)
      if (inflight) return await inflight
      const req = axios.get(`/portfolios/${id}/transaction-fee-analytics/`, {
        params: {
          ...(date_from ? { date_from } : {}),
          ...(date_to ? { date_to } : {}),
          group_by,
        },
      }).then((response) => {
        const data = response.data as TransactionFeeAnalytics
        if (!this._feeAnalyticsLastFetchedAt) this._feeAnalyticsLastFetchedAt = {}
        if (!this._feeAnalyticsCache) this._feeAnalyticsCache = {}
        this._feeAnalyticsLastFetchedAt[key] = Date.now()
        this._feeAnalyticsCache[key] = data
        return data
      }).finally(() => {
        this._feeAnalyticsInFlight?.delete(key)
      })
      this._feeAnalyticsInFlight.set(key, req)
      return await req
    },
    async fetchPortfolioPreviousDayVariation(id: number, options?: { forceRefresh?: boolean; ttlMs?: number }): Promise<{
      variation_percent: number,
      variation_value: number,
      today_value: number,
      yesterday_value: number,
      variation_percent_with_cash?: number,
      variation_value_with_cash?: number,
      start_date?: string | null,
      end_date?: string | null,
      method?: string | null,
      pending?: boolean,
      previous_date?: string | null,
      pending_message?: string | null,
      has_data?: boolean,
    }> {
      try {
        const t0 = Date.now()
        const forceRefresh = options?.forceRefresh === true
        const ttlMs = options?.ttlMs ?? 60000
        const lastFetched = this._prevDayVarLastFetchedAt?.[id]
        const now = Date.now()
        if (!forceRefresh && lastFetched && (now - lastFetched) < ttlMs) {
          // Retourner les données du cache si disponibles
          if (this._prevDayVarCache?.[id]) {
            if (this.diagnostics) this.diagnostics.prevDayVar.cache_hits = (this.diagnostics.prevDayVar.cache_hits || 0) + 1
            return this._prevDayVarCache[id]
          }
          // Sinon vérifier s'il y a une requête en cours
          if (!this._prevDayVarInFlight) {
            this._prevDayVarInFlight = new Map<number, Promise<PreviousDayVariation>>()
          }
          const inflight = this._prevDayVarInFlight.get(id)
          if (inflight) {
            if (this.diagnostics) this.diagnostics.prevDayVar.inflight_reuses += 1
            return await inflight
          }
        }
        if (!this._prevDayVarInFlight) {
          this._prevDayVarInFlight = new Map<number, Promise<PreviousDayVariation>>()
        }
        const inflight = this._prevDayVarInFlight.get(id)
        if (inflight) {
          if (this.diagnostics) this.diagnostics.prevDayVar.inflight_reuses += 1
          return await inflight
        }
        const req = axios.get(`/portfolios/${id}/previous_day_variation/`, {
          params: forceRefresh ? { no_cache: 1 } : undefined,
        }).then((response) => {
          if (!this._prevDayVarLastFetchedAt) {
            this._prevDayVarLastFetchedAt = {}
          }
          this._prevDayVarLastFetchedAt[id] = Date.now()
          // Stocker les données en cache pour accès immédiat par les computed properties
          if (!this._prevDayVarCache) {
            this._prevDayVarCache = {}
          }
          this._prevDayVarCache[id] = response.data
          return response.data
        }).finally(() => {
          if (this._prevDayVarInFlight) {
            this._prevDayVarInFlight.delete(id)
          }
          if (this.diagnostics) {
            this.diagnostics.prevDayVar.network_requests += 1
            this.diagnostics.prevDayVar.last_ms = Date.now() - t0
          }
        })
        this._prevDayVarInFlight.set(id, req)
        return await req
      } catch (error: unknown) {
        if (import.meta.env.DEV) {
          logger.error('Error fetching previous day variation:', error)
        }
        if (this.diagnostics) this.diagnostics.prevDayVar.errors += 1
        throw error
      }
    },
    async searchAssets(query: string, portfolioId?: number) {
      try {
        this.isLoading = true
        const response = await axios.get('/portfolio-assets/search/', {
          params: {
            q: query,
            portfolio_id: portfolioId
          }
        })
        this.error = null
        return response.data
      } catch (error: unknown) {
        this.error = 'Erreur lors de la recherche d\'actifs'
        if (import.meta.env.DEV) {
          logger.error('Error searching assets:', error)
        }
        throw error
      } finally {
        this.isLoading = false
      }
    },
    async fetchVisibilityTypes(): Promise<VisibilityType[]> {
      try {
        // Utiliser l'intercepteur global d'Axios plutôt que d'ajouter manuellement le token
        const response = await axios.get('/visibility-types/')
        this.visibilityTypes = response.data
        return response.data
      } catch (err: unknown) {
        if (import.meta.env.DEV) {
          logger.error('Erreur fetchVisibilityTypes:', {
            message: err instanceof Error ? err.message : String(err),
            response: isAxiosError(err) ? err.response?.data : undefined,
            status: isAxiosError(err) ? err.response?.status : undefined
          })
        }
        if (isAxiosError(err) && err.response?.status === 401) {
          const authStore = useAuthStore()
          await authStore.refreshToken()
          return this.fetchVisibilityTypes()
        }
        throw new Error('Impossible de récupérer les types de visibilité')
      }
    },
    // Méthode pour mettre à jour la visibilité d'un portefeuille
    async updatePortfolioVisibility(portfolioId: number, visibilityId: number): Promise<Portfolio> {
      try {
        // Validation du type de visibilité
        if (!this.visibilityTypes.some(type => type.id === visibilityId)) {
          throw new Error('Type de visibilité invalide')
        }
        // Utiliser l'intercepteur global d'Axios plutôt que d'ajouter manuellement le token
        const response = await axios.patch(
          `/portfolios/${portfolioId}/`,
          { visibility_id: visibilityId },
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        )
        // Mise à jour du portfolio dans le tableau portfolios
        const portfolioIndex = this.portfolios.findIndex(p => p.id === portfolioId)
        if (portfolioIndex !== -1) {
          this.portfolios[portfolioIndex] = {
            ...this.portfolios[portfolioIndex],
            visibility: response.data.visibility,
            visibility_id: visibilityId
          }
        }
        // Mise à jour du portfolio courant si nécessaire
        if (this.currentPortfolio?.id === portfolioId) {
          this.currentPortfolio = response.data
        }
        return response.data
      } catch (err: unknown) {
        if (import.meta.env.DEV) {
          logger.error('Erreur updatePortfolioVisibility:', {
            message: err instanceof Error ? err.message : String(err),
            response: isAxiosError(err) ? err.response?.data : undefined,
            status: isAxiosError(err) ? err.response?.status : undefined,
            config: isAxiosError(err) ? err.config : undefined,
            fullError: err
          })
        }
        if (isAxiosError(err) && err.response?.status === 401) {
          const authStore = useAuthStore()
          await authStore.refreshToken()
          return this.updatePortfolioVisibility(portfolioId, visibilityId)
        }
        let errorMessage = 'Erreur lors de la mise à jour de la visibilité'
        if (isAxiosError(err) && err.response?.data) {
          if (typeof err.response.data === 'string') {
            errorMessage += ` : ${err.response.data}`
          } else if (typeof err.response.data === 'object') {
            errorMessage += ` : ${JSON.stringify(err.response.data)}`
          }
        }
        throw new Error(errorMessage)
      }
    },
    updatePortfolioInStore(portfolioData: Portfolio) {
      const state = this.$state
      const index = state.portfolios.findIndex(p => p.id === portfolioData.id)
      if (index !== -1) {
        // Mettre à jour le portefeuille dans la liste
        state.portfolios[index] = portfolioData
        // Si c'est le portefeuille actuellement sélectionné, le mettre à jour aussi
        if (state.currentPortfolio?.id === portfolioData.id) {
          state.currentPortfolio = portfolioData
        }
      }
    },
    /**
     * Rafraîchit les actifs du portefeuille courant sans modifier directement la propriété computed
     * Cette méthode est utilisée pour forcer la réactivité sans modifier directement portfolio.value
     */
    refreshCurrentPortfolioAssets() {
      if (this.currentPortfolio && this.currentPortfolio.assets) {
        // Créer une nouvelle référence pour les actifs dans le store plutôt que dans le composant
        // Cela force Vue à recalculer les propriétés calculées qui dépendent de ces actifs
        const updatedPortfolio = { ...this.currentPortfolio }
        updatedPortfolio.assets = [...this.currentPortfolio.assets]
        this.currentPortfolio = updatedPortfolio
      }
    },
  }
})
